import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Loader2,
  Mail,
  Send,
  X,
} from 'lucide-react';
import { api } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';
import type { VisitEmailPreview } from '../../types';

interface VisitFollowupEmailModalProps {
  visitId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSent?: () => void;
}

function errorMessage(err: unknown, fallback: string): string {
  const response = (err as { response?: { data?: unknown } })?.response?.data;
  if (response && typeof response === 'object') {
    const error = (response as { error?: unknown }).error;
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object') {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string') return message;
    }
  }
  return fallback;
}

function isDuplicateError(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 409;
}

export const VisitFollowupEmailModal: React.FC<VisitFollowupEmailModalProps> = ({
  visitId,
  isOpen,
  onClose,
  onSent,
}) => {
  const [preview, setPreview] = useState<VisitEmailPreview | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [recipient, setRecipient] = useState('');
  const [introText, setIntroText] = useState('');
  const [loading, setLoading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const loadPreview = useCallback(
    async (productIds?: string[], nextIntro?: string) => {
      if (!visitId) return;
      const params = new URLSearchParams();
      if (productIds) params.set('product_ids', productIds.join(','));
      if (nextIntro?.trim()) params.set('intro_text', nextIntro.trim());
      const query = params.toString();
      const res = await api.get<VisitEmailPreview>(
        `/visits/${visitId}/followup-email/preview${query ? `?${query}` : ''}`
      );
      return res.data;
    },
    [visitId]
  );

  useEffect(() => {
    if (!isOpen || !visitId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSentTo(null);
    (async () => {
      try {
        const data = await loadPreview();
        if (cancelled || !data) return;
        setPreview(data);
        setSelectedIds(data.content.selected_product_ids);
        setRecipient(data.content.recipient ?? '');
        setIntroText(data.content.intro_text);
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, 'Vorschau konnte nicht geladen werden.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, visitId, loadPreview]);

  // Re-render server-side so the preview always equals what will actually be sent.
  const refreshPreview = async (productIds: string[], intro: string) => {
    setRendering(true);
    setError(null);
    try {
      const data = await loadPreview(productIds, intro);
      if (data) setPreview(data);
    } catch (err) {
      setError(errorMessage(err, 'Vorschau konnte nicht aktualisiert werden.'));
    } finally {
      setRendering(false);
    }
  };

  const toggleProduct = (productId: string) => {
    const next = selectedIds.includes(productId)
      ? selectedIds.filter((id) => id !== productId)
      : [...selectedIds, productId];
    setSelectedIds(next);
    void refreshPreview(next, introText);
  };

  const handleSend = async (allowDuplicate = false) => {
    if (!visitId) return;
    setSending(true);
    setError(null);
    setDuplicateWarning(null);
    try {
      const res = await api.post<{ recipient: string }>(
        `/visits/${visitId}/followup-email`,
        {
          product_ids: selectedIds,
          ...(recipient.trim() ? { recipient: recipient.trim() } : {}),
          ...(introText.trim() ? { intro_text: introText.trim() } : {}),
          ...(allowDuplicate ? { allow_duplicate: true } : {}),
        }
      );
      setSentTo(res.data.recipient);
      onSent?.();
    } catch (err) {
      const message = errorMessage(err, 'E-Mail konnte nicht gesendet werden.');
      if (isDuplicateError(err)) {
        setDuplicateWarning(message);
      } else {
        setError(message);
      }
    } finally {
      setSending(false);
    }
  };

  if (!isOpen || !visitId) return null;

  const content = preview?.content;
  const canSend =
    !!recipient.trim() && selectedIds.length > 0 && !sending && !rendering;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <Mail size={20} className="text-teal-700 shrink-0" />
            <h2 className="font-black text-gray-900 truncate">
              Nachfass-E-Mail{content ? ` · ${content.pos_name}` : ''}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 text-gray-500 hover:text-gray-900"
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-gray-600 py-8 justify-center">
              <Loader2 size={18} className="animate-spin" />
              Vorschau wird geladen…
            </div>
          )}

          {sentTo && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
              <CheckCircle size={18} className="text-emerald-700 mt-0.5 shrink-0" />
              <p className="text-sm text-emerald-900 font-medium">
                E-Mail wurde an {sentTo} gesendet. Eine Kopie liegt im Postfach
                mail@brandcircuit.de.
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
              <AlertTriangle size={18} className="text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {duplicateWarning && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-300">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-900">{duplicateWarning}</p>
              </div>
              <button
                type="button"
                onClick={() => void handleSend(true)}
                disabled={sending}
                className="mt-2 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-bold
                  disabled:opacity-50"
              >
                Trotzdem erneut senden
              </button>
            </div>
          )}

          {content?.warnings.map((warning) => (
            <div
              key={warning}
              className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200"
            >
              <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-900">{warning}</p>
            </div>
          ))}

          {content && !sentTo && (
            <>
              <label className="block">
                <span className="block text-sm font-bold text-gray-900 mb-1">
                  Empfänger
                </span>
                <input
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="kontakt@pos.de"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-base"
                />
                {content.recipient_candidates.length > 1 && (
                  <span className="mt-1 flex flex-wrap gap-2">
                    {content.recipient_candidates.map((candidate) => (
                      <button
                        key={candidate}
                        type="button"
                        onClick={() => setRecipient(candidate)}
                        className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700"
                      >
                        {candidate}
                      </button>
                    ))}
                  </span>
                )}
              </label>

              <label className="block">
                <span className="block text-sm font-bold text-gray-900 mb-1">
                  Einleitung
                </span>
                <textarea
                  value={introText}
                  onChange={(e) => setIntroText(e.target.value)}
                  onBlur={() => void refreshPreview(selectedIds, introText)}
                  rows={4}
                  maxLength={2000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-base resize-y"
                />
                <span className="mt-1 block text-xs text-gray-500">
                  Leerzeile trennt Absätze. Die Anrede und die Signatur werden automatisch
                  ergänzt.
                </span>
              </label>

              <div>
                <p className="text-sm font-bold text-gray-900 mb-2">
                  Produkte in der E-Mail
                </p>
                <div className="space-y-2">
                  {content.available_products.length === 0 && (
                    <p className="text-sm text-gray-500">
                      Bei diesem Besuch wurden keine Produkte erfasst.
                    </p>
                  )}
                  {content.available_products.map((option) => (
                    <label
                      key={option.product_id}
                      className="flex items-start gap-3 p-3 rounded-xl border border-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(option.product_id)}
                        onChange={() => toggleProduct(option.product_id)}
                        className="mt-0.5 w-5 h-5 shrink-0"
                      />
                      <span className="min-w-0">
                        <span className="block font-bold text-gray-900">
                          {option.brand_name}
                        </span>
                        {option.reasons.length > 0 ? (
                          <span className="block text-xs text-teal-700 font-medium">
                            {option.reasons.join(' · ')}
                          </span>
                        ) : (
                          <span className="block text-xs text-gray-500">
                            Nicht als Probe erfasst
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-gray-900">Vorschau</p>
                  {rendering && (
                    <Loader2 size={16} className="animate-spin text-gray-400" />
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-2 break-words">
                  Betreff: {preview?.subject}
                </p>
                <iframe
                  title="E-Mail-Vorschau"
                  sandbox=""
                  srcDoc={preview?.html ?? ''}
                  className="w-full h-80 border border-gray-200 rounded-xl bg-white"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Produktbilder werden erst beim Versand eingebettet und sind in der
                  Vorschau leer.
                </p>
              </div>

              <p className="text-xs text-gray-500">
                Technischer Absender ist delivery@brandcircuit.de. Antworten der Kunden
                gehen an mail@brandcircuit.de. Signatur zeigt{' '}
                {content.signature.email}.
              </p>

              {content.previous_sends.length > 0 && (
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                  <p className="text-sm font-bold text-gray-900 mb-1">
                    Bereits gesendet
                  </p>
                  <ul className="space-y-1">
                    {content.previous_sends.map((send) => (
                      <li
                        key={`${send.sent_at}-${send.recipient_email}`}
                        className="text-xs text-gray-600"
                      >
                        {formatDateTime(send.sent_at)} · {send.recipient_email} ·{' '}
                        {send.status === 'sent' ? 'gesendet' : 'fehlgeschlagen'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 font-bold text-gray-700"
          >
            {sentTo ? 'Schließen' : 'Abbrechen'}
          </button>
          {!sentTo && (
            <button
              onClick={() => void handleSend()}
              disabled={!canSend}
              className="flex-1 px-4 py-3 rounded-xl bg-teal-700 text-white font-bold
                flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
              Senden
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
